import { useEffect, useMemo, useRef, useState } from 'react';
import { Caption1, Input, MessageBar, MessageBarBody, Spinner, Text, tokens } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { PageHeader } from '../components/PageHeader';
import { formatBytes } from '../util/bytes';
import type { GraphConnectionsDto, GraphNodeDto, GraphSearchHitDto, OneDriveUserDto } from '../api/types';
import './usergraph.css';

const SVGNS = 'http://www.w3.org/2000/svg';
const MAX_ITEMS = 30;

const TYPE_COLOR: Record<string, string> = {
  user: '#D85A30',
  group: '#378ADD',
  device: '#1D9E75',
  license: '#7F77DD',
  role: '#BA7517',
};
const TYPE_LABEL: Record<string, string> = {
  user: 'Benutzer',
  group: 'Gruppe',
  device: 'Gerät',
  license: 'Lizenz',
  role: 'Rolle',
};
const KIND_ORDER = ['member', 'owner', 'manager', 'report', 'device', 'license', 'role', 'other'];
const KIND_LABEL: Record<string, string> = {
  member: 'Mitgliedschaften',
  owner: 'Owner',
  manager: 'Manager',
  report: 'Direct Reports',
  device: 'Geräte',
  license: 'Lizenzen',
  role: 'Rollen',
  other: 'Weitere',
};

const initial = (s: string) => (s || '?').charAt(0).toUpperCase();

interface Spec {
  id: string;
  label: string;
  glyph: string;
  color: string;
  cls: string;
  open?: boolean;
  x: number;
  y: number;
  onClick: () => void;
  onDbl?: () => void;
}
interface Group {
  kind: string;
  label: string;
  color: string;
  items: GraphNodeDto[];
}
interface Engine {
  render: (nodes: Spec[], edges: { from: string; to: string }[]) => void;
  destroy: () => void;
}

function createEngine(container: HTMLElement): Engine {
  const svg = document.createElementNS(SVGNS, 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.style.position = 'absolute';
  svg.style.inset = '0';
  container.appendChild(svg);

  const els: Record<string, HTMLDivElement> = {};
  const circ: Record<string, HTMLDivElement> = {};
  let lines: [SVGLineElement, string, string][] = [];

  const ctr = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const c = container.getBoundingClientRect();
    return { x: r.left + r.width / 2 - c.left, y: r.top + r.height / 2 - c.top };
  };

  function make(id: string): HTMLDivElement {
    const cx = container.clientWidth / 2;
    const cy = container.clientHeight / 2;
    const w = document.createElement('div');
    w.className = 'ug-node';
    w.style.left = `${cx}px`;
    w.style.top = `${cy}px`;
    w.style.opacity = '0';
    const c = document.createElement('div');
    c.className = 'ug-circ';
    const l = document.createElement('div');
    l.className = 'ug-lbl';
    w.appendChild(c);
    w.appendChild(l);
    container.appendChild(w);
    els[id] = w;
    circ[id] = c;
    return w;
  }

  function render(nodes: Spec[], edges: { from: string; to: string }[]) {
    const cx = container.clientWidth / 2;
    const cy = container.clientHeight / 2;
    const vis = new Set(nodes.map((n) => n.id));

    Object.keys(els).forEach((k) => {
      if (!vis.has(k)) {
        const w = els[k];
        w.style.opacity = '0';
        w.style.left = `${cx}px`;
        w.style.top = `${cy}px`;
        window.setTimeout(() => w.remove(), 420);
        delete els[k];
        delete circ[k];
      }
    });

    nodes.forEach((n) => {
      if (!els[n.id]) make(n.id);
    });
    void container.offsetWidth;

    nodes.forEach((n) => {
      const w = els[n.id];
      const c = circ[n.id];
      w.className = `ug-node ${n.cls}${n.open ? ' open' : ''}`;
      c.style.borderColor = n.color;
      c.style.color = n.color;
      c.textContent = n.glyph;
      (w.lastChild as HTMLDivElement).textContent = n.label;
      w.style.left = `${n.x}px`;
      w.style.top = `${n.y}px`;
      w.style.opacity = '1';
      w.onclick = n.onClick;
      w.ondblclick = n.onDbl ?? null;
    });

    svg.innerHTML = '';
    lines = [];
    edges.forEach((e) => {
      if (els[e.from] && els[e.to]) {
        const ln = document.createElementNS(SVGNS, 'line');
        ln.setAttribute('class', 'ug-edge');
        svg.appendChild(ln);
        lines.push([ln, e.from, e.to]);
      }
    });

    const t0 = performance.now();
    const tick = (t: number) => {
      lines.forEach(([ln, a, b]) => {
        if (!circ[a] || !circ[b]) return;
        const p = ctr(circ[a]);
        const q = ctr(circ[b]);
        ln.setAttribute('x1', `${p.x}`);
        ln.setAttribute('y1', `${p.y}`);
        ln.setAttribute('x2', `${q.x}`);
        ln.setAttribute('y2', `${q.y}`);
      });
      if (t - t0 < 600) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  return {
    render,
    destroy: () => {
      container.querySelectorAll('.ug-node').forEach((e) => e.remove());
      svg.remove();
    },
  };
}

type Detail = { mode: 'node'; conn: GraphConnectionsDto } | { mode: 'hub'; label: string; color: string; items: GraphNodeDto[] };

export function UserGraphPage() {
  const api = useApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<GraphSearchHitDto[]>([]);
  const [conn, setConn] = useState<GraphConnectionsDto | null>(null);
  const [expandedKind, setExpandedKind] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groups = useMemo<Group[]>(() => {
    if (!conn) return [];
    const byKind: Record<string, GraphNodeDto[]> = {};
    conn.neighbors.forEach((nb) => {
      const kind = conn.edges.find((e) => e.to === nb.id)?.kind ?? 'other';
      (byKind[kind] ||= []).push(nb);
    });
    return KIND_ORDER.filter((k) => byKind[k]).map((k) => ({
      kind: k,
      label: KIND_LABEL[k] ?? k,
      color: TYPE_COLOR[byKind[k][0].type] ?? '#888888',
      items: byKind[k],
    }));
  }, [conn]);

  async function focusNode(type: string, id: string) {
    setLoading(true);
    setError(null);
    try {
      const c = await api.graphConnections(type, id);
      setConn(c);
      setExpandedKind(null);
      setDetail({ mode: 'node', conn: c });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function peek(node: GraphNodeDto) {
    try {
      setDetail({ mode: 'node', conn: await api.graphConnections(node.type, node.id) });
    } catch {
      /* ignore */
    }
  }

  function toggleHub(g: Group) {
    setExpandedKind((prev) => (prev === g.kind ? null : g.kind));
    setDetail({ mode: 'hub', label: g.label, color: g.color, items: g.items });
  }

  useEffect(() => {
    if (containerRef.current && !engineRef.current) {
      engineRef.current = createEngine(containerRef.current);
    }
    return () => {
      engineRef.current?.destroy();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    const c = containerRef.current;
    const eng = engineRef.current;
    if (!c || !eng || !conn) return;
    const id = requestAnimationFrame(() => {
      const W = c.clientWidth;
      const H = c.clientHeight;
      const cx = W / 2;
      const cy = H / 2;
      const Rmin = Math.min(cx, cy);
      const R1 = Rmin * 0.46;
      const R2 = Rmin * 0.86;
      const nodes: Spec[] = [];
      const edges: { from: string; to: string }[] = [];

      nodes.push({
        id: conn.node.id,
        label: conn.node.label,
        glyph: initial(conn.node.label),
        color: TYPE_COLOR[conn.node.type] ?? '#888888',
        cls: 'focus',
        x: cx,
        y: cy,
        onClick: () => setDetail({ mode: 'node', conn }),
      });

      const Hn = groups.length || 1;
      groups.forEach((g, i) => {
        const ang = ((-90 + (i * 360) / Hn) * Math.PI) / 180;
        const hubId = `hub:${g.kind}`;
        nodes.push({
          id: hubId,
          label: g.label,
          glyph: String(g.items.length),
          color: g.color,
          cls: 'hub',
          open: expandedKind === g.kind,
          x: cx + R1 * Math.cos(ang),
          y: cy + R1 * Math.sin(ang),
          onClick: () => toggleHub(g),
          onDbl: () => toggleHub(g),
        });
        edges.push({ from: conn.node.id, to: hubId });

        if (expandedKind === g.kind) {
          const items = g.items.slice(0, MAX_ITEMS);
          const M = items.length;
          const spread = (Math.min(150, Math.max(24, M * 7)) * Math.PI) / 180;
          const a0 = ang - spread / 2;
          items.forEach((it, j) => {
            const a = M > 1 ? a0 + j * (spread / (M - 1)) : ang;
            nodes.push({
              id: it.id,
              label: it.label,
              glyph: initial(it.label),
              color: TYPE_COLOR[it.type] ?? '#888888',
              cls: 'item',
              x: cx + R2 * Math.cos(a),
              y: cy + R2 * Math.sin(a),
              onClick: () => peek(it),
              onDbl: () => focusNode(it.type, it.id),
            });
            edges.push({ from: hubId, to: it.id });
          });
        }
      });

      eng.render(nodes, edges);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn, groups, expandedKind, detail !== null]);

  function onInput(value: string) {
    setQuery(value);
    if (timer.current) window.clearTimeout(timer.current);
    if (value.trim().length < 2) {
      setHits([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      api.graphSearch(value.trim()).then(setHits).catch(() => setHits([]));
    }, 300);
  }

  function pickHit(h: GraphSearchHitDto) {
    setHits([]);
    setQuery(h.label);
    focusNode(h.type, h.id);
  }

  return (
    <div>
      <PageHeader
        title="User-Graph"
        description="Beziehungen rund um einen Benutzer, eine Gruppe oder ein Gerät. Satelliten je Thema aufklappen (Klick), Doppelklick auf einen Eintrag rückt ihn in den Fokus."
      />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <Input
            style={{ width: 360 }}
            contentBefore={<SearchRegular />}
            placeholder="User, Gruppe oder Gerät suchen…"
            value={query}
            onChange={(_, d) => onInput(d.value)}
          />
          {hits.length > 0 ? (
            <div className="ug-hits">
              {hits.map((h) => (
                <div className="ug-hit" key={`${h.type}-${h.id}`} onClick={() => pickHit(h)}>
                  <span className="ug-dot" style={{ background: TYPE_COLOR[h.type] ?? '#888' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="pii" style={{ fontWeight: 500 }}>
                      {h.label}
                    </span>
                    {h.sub ? <span className="pii" style={{ color: tokens.colorNeutralForeground3 }}> · {h.sub}</span> : null}
                  </span>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{TYPE_LABEL[h.type] ?? h.type}</Caption1>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        {loading ? <Spinner size="tiny" /> : null}
        {conn ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Fokus: {conn.node.label}</Caption1> : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, fontSize: 12, color: tokens.colorNeutralForeground3, flexWrap: 'wrap' }}>
          {Object.keys(TYPE_LABEL).map((t) => (
            <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span className="ug-dot" style={{ border: `2px solid ${TYPE_COLOR[t]}`, background: 'transparent' }} />
              {TYPE_LABEL[t]}
            </span>
          ))}
        </div>
      </div>

      {error ? (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: detail ? '1fr 320px' : '1fr', gap: 14, alignItems: 'start' }}>
        <div className="ug-canvas" ref={containerRef}>
          {!conn && !loading ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Suche oben einen Benutzer, eine Gruppe oder ein Gerät, um den Graphen zu starten.
              </Caption1>
            </div>
          ) : null}
        </div>

        {detail ? <DetailPanel detail={detail} onFocus={focusNode} /> : null}
      </div>
    </div>
  );
}

function Chips({ items, onFocus, kindOf }: { items: GraphNodeDto[]; onFocus: (t: string, id: string) => void; kindOf?: (id: string) => string | undefined }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {items.slice(0, 60).map((nb) => (
        <span
          className="ug-chip pii"
          key={nb.id}
          title={`${TYPE_LABEL[nb.type] ?? nb.type}${kindOf && kindOf(nb.id) ? ` · ${KIND_LABEL[kindOf(nb.id)!] ?? kindOf(nb.id)}` : ''}`}
          onClick={() => onFocus(nb.type, nb.id)}
        >
          <span className="ug-dot" style={{ display: 'inline-block', marginRight: 5, background: TYPE_COLOR[nb.type] ?? '#888' }} />
          {nb.label}
        </span>
      ))}
      {items.length > 60 ? <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>+{items.length - 60} weitere</Caption1> : null}
    </div>
  );
}

function OneDriveNodeKpis({ userId }: { userId: string }) {
  const api = useApi();
  const [data, setData] = useState<OneDriveUserDto | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    setData(null);
    setErr(false);
    api.getOneDriveUserById(userId).then((d) => active && setData(d)).catch(() => active && setErr(true));
    return () => {
      active = false;
    };
  }, [api, userId]);

  if (err) return null;
  if (!data) return <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: 10 }}>OneDrive lädt…</Caption1>;
  const a = data.account;
  if (!a) return null;

  const cell = (label: string, value: string) => (
    <div style={{ background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, padding: '6px 10px' }}>
      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ marginBottom: 10 }}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>OneDrive</Caption1>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        {cell('Genutzt', formatBytes(a.storageUsedBytes))}
        {cell('Auslastung', `${a.percentUsed}%`)}
        {cell('Dateien', a.fileCount.toLocaleString('de-DE'))}
        {cell('Letzte Aktivität', a.lastActivity ? new Date(a.lastActivity).toLocaleDateString('de-DE') : '—')}
      </div>
    </div>
  );
}

function DetailPanel({ detail, onFocus }: { detail: Detail; onFocus: (t: string, id: string) => void }) {
  if (detail.mode === 'hub') {
    return (
      <div style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, padding: 14, background: tokens.colorNeutralBackground1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span className="ug-dot" style={{ border: `2px solid ${detail.color}`, background: 'transparent', width: 14, height: 14 }} />
          <Text weight="semibold">{detail.label}</Text>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>({detail.items.length})</Caption1>
        </div>
        <Chips items={detail.items} onFocus={onFocus} />
      </div>
    );
  }

  const node = detail.conn.node;
  const color = TYPE_COLOR[node.type] ?? '#888';
  return (
    <div style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, padding: 14, background: tokens.colorNeutralBackground1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: `2px solid ${color}`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
          {initial(node.label)}
        </div>
        <div style={{ minWidth: 0 }}>
          <Text weight="semibold" className="pii" style={{ display: 'block' }}>
            {node.label}
          </Text>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{TYPE_LABEL[node.type] ?? node.type}</Caption1>
        </div>
      </div>

      {node.fields && node.fields.length > 0 ? (
        <table style={{ width: '100%', fontSize: 13, marginBottom: 10 }}>
          <tbody>
            {node.fields.map((f, i) => (
              <tr key={i}>
                <td style={{ color: tokens.colorNeutralForeground3, padding: '3px 0', verticalAlign: 'top' }}>{f.key}</td>
                <td className="pii" style={{ textAlign: 'right', padding: '3px 0' }}>{f.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {node.type === 'user' ? <OneDriveNodeKpis userId={node.id} /> : null}

      {detail.conn.neighbors.length > 0 ? (
        <div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Verbindungen ({detail.conn.neighbors.length})</Caption1>
          <Chips items={detail.conn.neighbors} onFocus={onFocus} kindOf={(id) => detail.conn.edges.find((e) => e.to === id)?.kind} />
        </div>
      ) : null}
    </div>
  );
}
